import MuleBadge from '../../components/MuleBadge';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronRightIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { getParkingColorStyles } from '../../components/ParkingLocationModal';
import { Tooltip } from '../../components/ui/Tooltip';
import { WORKING_CATEGORY_GROUNDED_YARD } from '../../constants/workingCategories';
import {
  startedState,
  startedSortKey,
  formatStartedTime,
  summarizeVisible,
  type GroundedYardDriverStats,
  type GroundedYardStatsResponse,
} from './groundedYardStats';

interface DriverData {
  driver: string;
  driver_name: string;
  isMule?: boolean;
  parkingloc: number | null;
  parking_location_name: string;
  parking_color: number | null;
  region: string;
  dispatcher_region?: string;
  comments: string;
}

interface DispatcherGroup {
  dispatcher: string;
  region: string;
  drivers: DriverData[];
}

interface DriverGroundedYardProps {
  selectedDispatchers: string[];
  selectedRegions: string[];
  selectedDate: Date | null;
  refreshTrigger: number;
  groupByDispatcher: boolean;
  usernameToDisplayname: Map<string, string>;
}

type SortColumn =
  | 'driver'
  | 'parking'
  | 'region'
  | 'started'
  | 'cy'
  | 'open'
  | 'declined'
  | 'comments';

// Why the four stats columns are showing dashes (if they are).
type StatsState = 'loading' | 'ok' | 'unavailable' | 'error';

const STATS_ENDPOINT = '/driver-dispatch/grounded-yard-stats';
const DASH = '-';

// Extract the string value used to sort a driver row for a given column
const getSortValue = (
  driver: DriverData,
  column: SortColumn,
  stats: GroundedYardDriverStats | undefined
): string => {
  switch (column) {
    case 'driver':
      return driver.driver_name || '';
    case 'parking':
      return driver.parking_location_name || '';
    case 'region':
      return driver.region || '';
    case 'started':
      return startedSortKey(stats);
    case 'cy':
      return stats ? String(stats.cy_picked) : '';
    case 'open':
      return stats ? String(stats.open_legs.length) : '';
    case 'declined':
      return stats ? String(stats.declined.length) : '';
    case 'comments':
      return driver.comments || '';
    default:
      return '';
  }
};

// Format date as YYYY-MM-DD
const formatDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const COLUMNS: { key: SortColumn; label: string; className: string }[] = [
  { key: 'parking', label: 'Parking Location', className: 'w-48' },
  { key: 'region', label: 'Region', className: 'w-32' },
  { key: 'started', label: 'Started', className: 'w-36' },
  { key: 'cy', label: 'CY Pickups', className: 'w-28' },
  { key: 'open', label: 'Open Legs', className: 'w-24' },
  { key: 'declined', label: 'Declined', className: 'w-24' },
  { key: 'comments', label: 'Comments', className: '' },
];

const DriverGroundedYard: React.FC<DriverGroundedYardProps> = ({
  selectedDispatchers,
  selectedRegions,
  selectedDate,
  refreshTrigger,
  groupByDispatcher,
  usernameToDisplayname,
}) => {
  const [data, setData] = useState<DispatcherGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<GroundedYardStatsResponse | null>(null);
  const [statsState, setStatsState] = useState<StatsState>('loading');
  // Ref, NOT state: a state flag as an effect dep re-ran the fetch after the
  // first load (see DriverDeclinedMoves). Background refreshes (30s timer)
  // update in place without flashing "Loading...".
  const initialLoadCompleteRef = useRef(false);
  const [expandedDispatchers, setExpandedDispatchers] = useState<Set<string>>(new Set());
  const [sortColumn, setSortColumn] = useState<SortColumn>('driver');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const statsByDriver = stats?.drivers ?? {};

  // Sort a list of drivers by the active column/direction
  const sortDrivers = <T extends DriverData>(drivers: T[]): T[] => {
    return [...drivers].sort((a, b) => {
      const cmp = getSortValue(a, sortColumn, statsByDriver[a.driver]).localeCompare(
        getSortValue(b, sortColumn, statsByDriver[b.driver]),
        undefined,
        { numeric: true, sensitivity: 'base' }
      );
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  };

  // Fetch grounded yard drivers (roster) + the day's leg stats, together.
  // The two calls fail independently: a stats problem never hides the roster.
  useEffect(() => {
    if (!selectedDate) return;
    const dateStr = formatDateKey(selectedDate);
    const isBackgroundRefresh = initialLoadCompleteRef.current;
    let cancelled = false;

    const fetchRoster = async () => {
      if (!isBackgroundRefresh) setLoading(true);
      try {
        // Use the existing driver status endpoint
        const response = await api.get('/safety/status/weekly', {
          params: {
            start_date: dateStr,
            end_date: dateStr,
          },
        });
        if (cancelled) return;

        // Filter for Grounded Yard drivers (Available status with Grounded Yard working category)
        const groundedYardData: DispatcherGroup[] = [];

        for (const group of response.data) {
          const groundedDrivers: DriverData[] = [];

          for (const driver of group.drivers) {
            const statusInfo = driver.statuses?.[dateStr];
            if (statusInfo?.status === 'Available' && statusInfo?.workingcategory === WORKING_CATEGORY_GROUNDED_YARD) {
              groundedDrivers.push({
                driver: driver.driver,
                driver_name: driver.driver_name,
                isMule: driver.isMule,
                parkingloc: driver.parkingloc,
                parking_location_name: driver.parking_location_name,
                parking_color: driver.parking_color,
                region: driver.region,
                dispatcher_region: group.region,
                comments: statusInfo.comments || '',
              });
            }
          }

          if (groundedDrivers.length > 0) {
            groundedYardData.push({
              dispatcher: group.dispatcher,
              region: group.region,
              drivers: groundedDrivers,
            });
          }
        }

        setData(groundedYardData);
        // Auto-expand all dispatchers
        setExpandedDispatchers(new Set(groundedYardData.map(g => g.dispatcher)));
        initialLoadCompleteRef.current = true;
      } catch (error) {
        console.error('Error fetching grounded yard drivers:', error);
      } finally {
        if (!cancelled && !isBackgroundRefresh) setLoading(false);
      }
    };

    const fetchStats = async () => {
      try {
        const response = await api.get(STATS_ENDPOINT, { params: { date: dateStr } });
        if (cancelled) return;
        const body: GroundedYardStatsResponse = response.data;
        setStats(body);
        setStatsState(body?.stats_available ? 'ok' : 'unavailable');
      } catch (error) {
        console.error('Error fetching grounded yard stats:', error);
        if (cancelled) return;
        // Keep whatever we last had on a background refresh; on a fresh load
        // there is nothing to keep.
        if (!isBackgroundRefresh) setStats(null);
        setStatsState('error');
      }
    };

    fetchRoster();
    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [selectedDate, refreshTrigger]);

  // Filter data by selected dispatchers and regions
  const filteredData = useMemo(() => {
    return data
      .filter(group => {
        if (selectedDispatchers.length > 0) {
          const selectedDisplaynames = new Set(
            selectedDispatchers.map(username => usernameToDisplayname.get(username) || username)
          );
          if (!selectedDisplaynames.has(group.dispatcher)) return false;
        }
        if (selectedRegions.length > 0 && !selectedRegions.includes(group.region)) {
          return false;
        }
        return true;
      })
      .map(group => ({
        ...group,
        drivers: group.drivers,
      }));
  }, [data, selectedDispatchers, selectedRegions, usernameToDisplayname]);

  // Grouped view: sort drivers within each dispatcher group
  const sortedGroups = useMemo(() => {
    return filteredData.map(group => ({
      ...group,
      drivers: sortDrivers(group.drivers),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, sortColumn, sortDirection, stats]);

  // Get flat list of all drivers when not grouping
  const flatDriverList = useMemo(() => {
    if (groupByDispatcher) return [];
    const flat = filteredData.flatMap(group =>
      group.drivers.map(driver => ({
        ...driver,
        dispatcher: group.dispatcher,
        dispatcher_region: group.region,
      }))
    );
    return sortDrivers(flat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, groupByDispatcher, sortColumn, sortDirection, stats]);

  // Footer totals over the drivers actually on screen (after filters)
  const visibleTotals = useMemo(() => {
    const visible = filteredData.flatMap(group => group.drivers);
    return summarizeVisible(visible.map(d => statsByDriver[d.driver]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, stats]);

  const totalCount = visibleTotals.drivers;
  const statsOk = statsState === 'ok';

  // Sort indicator shown in column headers
  const renderSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <span className="text-slate-400 text-xs leading-none">↕</span>;
    }
    return sortDirection === 'asc' ? (
      <ChevronUpIcon className="h-3.5 w-3.5" />
    ) : (
      <ChevronDownIcon className="h-3.5 w-3.5" />
    );
  };

  const toggleDispatcher = (dispatcher: string) => {
    setExpandedDispatchers(prev => {
      const next = new Set(prev);
      if (next.has(dispatcher)) {
        next.delete(dispatcher);
      } else {
        next.add(dispatcher);
      }
      return next;
    });
  };

  // ---- Stats cells -------------------------------------------------------

  const renderStarted = (s: GroundedYardDriverStats | undefined) => {
    if (!statsOk) {
      return <span data-testid="started-state" className="text-gray-400">{DASH}</span>;
    }
    const state = startedState(s);
    if (state === 'started') {
      return (
        <span
          data-testid="started-state"
          className="inline-flex items-center px-2 py-0.5 rounded text-sm bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
        >
          Started {formatStartedTime(s!.started_at)}
        </span>
      );
    }
    if (state === 'flag-only') {
      return (
        <span
          data-testid="started-state"
          className="inline-flex items-center px-2 py-0.5 rounded text-sm bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
        >
          Started
        </span>
      );
    }
    return (
      <span
        data-testid="started-state"
        className="inline-flex items-center px-2 py-0.5 rounded text-sm bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
      >
        Not started
      </span>
    );
  };

  const yardLabel = stats?.yard_labels?.join(' / ') || 'CY';

  const renderCyPickups = (s: GroundedYardDriverStats | undefined) => {
    if (!statsOk || !s || s.cy_planned === 0) {
      return <span data-testid="cy-pickups" className="text-gray-400">{DASH}</span>;
    }
    return (
      <Tooltip
        placement="top"
        content={
          <div className="text-xs">
            <div className="font-medium mb-1">From {yardLabel}: {s.cy_picked} picked of {s.cy_planned}</div>
            {s.cy_legs.map(l => (
              <div key={`${l.pronumber}-${l.legnum}`}>
                #{l.pronumber} → {l.dest || '?'} · {l.status}
              </div>
            ))}
          </div>
        }
      >
        <span data-testid="cy-pickups" className="font-medium text-heading cursor-help">
          {s.cy_picked} / {s.cy_planned}
        </span>
      </Tooltip>
    );
  };

  const renderOpenLegs = (s: GroundedYardDriverStats | undefined) => {
    if (!statsOk || !s || s.open_legs.length === 0) {
      return <span data-testid="open-legs-count" className="text-gray-400">{DASH}</span>;
    }
    return (
      <Tooltip
        placement="top"
        content={
          <div className="text-xs">
            <div className="font-medium mb-1">Not yet complete</div>
            {s.open_legs.map(l => (
              <div key={`${l.pronumber}-${l.legnum}`}>
                #{l.pronumber} {l.orig || '?'} → {l.dest || '?'} · {l.status}
              </div>
            ))}
          </div>
        }
      >
        <span data-testid="open-legs-count" className="font-medium text-heading cursor-help">
          {s.open_legs.length}
        </span>
      </Tooltip>
    );
  };

  const renderDeclined = (s: GroundedYardDriverStats | undefined) => {
    if (!statsOk || !s || s.declined.length === 0) {
      return <span data-testid="declined-count" className="text-gray-400">{DASH}</span>;
    }
    return (
      <Tooltip
        placement="top"
        content={
          <div className="text-xs">
            {s.declined.map((d, idx) => (
              <div key={`${d.pronumber}-${idx}`} className={idx > 0 ? 'mt-1' : ''}>
                <span className="font-medium">
                  {formatStartedTime(d.rejectdatetime)} #{d.pronumber}
                </span>{' '}
                {d.orig || '?'} → {d.dest || '?'}
                <div>{d.rejectreason}{d.rejectcomment ? ` — ${d.rejectcomment}` : ''}</div>
              </div>
            ))}
          </div>
        }
      >
        <span data-testid="declined-count" className="font-semibold text-red-600 dark:text-red-400 cursor-help">
          {s.declined.length}
        </span>
      </Tooltip>
    );
  };

  const renderDriverRow = (driver: DriverData, indent: boolean) => {
    const s = statsByDriver[driver.driver];
    return (
      <tr
        key={driver.driver}
        className="border-b border-rule hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        <td className={`p-3 border-r border-rule ${indent ? 'pl-8' : ''}`}>
          <div className="font-medium text-heading">
            {driver.driver_name}<MuleBadge isMule={driver.isMule} />
          </div>
          <div className="text-xs text-muted">
            {driver.driver}
          </div>
        </td>
        <td className="p-3 border-r border-rule">
          {driver.parking_location_name ? (
            <span
              className="inline-flex items-center px-2 py-1 rounded text-sm"
              style={getParkingColorStyles(driver.parking_color) || undefined}
            >
              {driver.parking_location_name}
            </span>
          ) : (
            <span className="text-gray-400">{DASH}</span>
          )}
        </td>
        <td className="p-3 border-r border-rule text-label">
          {driver.region || DASH}
        </td>
        <td className="p-3 border-r border-rule">{renderStarted(s)}</td>
        <td className="p-3 border-r border-rule text-center">{renderCyPickups(s)}</td>
        <td className="p-3 border-r border-rule text-center">{renderOpenLegs(s)}</td>
        <td className="p-3 border-r border-rule text-center">{renderDeclined(s)}</td>
        <td className="p-3 text-label">
          {driver.comments || DASH}
        </td>
      </tr>
    );
  };

  const statsNote =
    statsState === 'unavailable'
      ? 'Leg stats are only available for dates within 7 days of today.'
      : statsState === 'error'
        ? 'Leg stats could not be loaded. The driver list is still current.'
        : null;

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-muted">Loading...</div>
      </div>
    );
  }

  if (totalCount === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-muted">
          No drivers marked as Grounded Yard for {selectedDate ? formatDateKey(selectedDate) : 'selected date'}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg overflow-auto mb-16">
      <table className="w-full border-collapse">
        <thead className="bg-slate-600 dark:bg-slate-700 text-white sticky top-0 z-10">
          <tr>
            <th className="p-3 text-left font-semibold border-r border-slate-500 w-64">
              <button
                type="button"
                onClick={() => handleSort('driver')}
                aria-label="Sort by Driver"
                className="flex items-center gap-1 font-semibold hover:text-slate-200 select-none w-full"
              >
                {groupByDispatcher ? 'Dispatcher / Driver' : 'Driver'}
                {renderSortIcon('driver')}
              </button>
            </th>
            {COLUMNS.map((col, idx) => (
              <th
                key={col.key}
                className={`p-3 text-left font-semibold ${idx < COLUMNS.length - 1 ? 'border-r border-slate-500' : ''} ${col.className}`}
              >
                <button
                  type="button"
                  onClick={() => handleSort(col.key)}
                  aria-label={`Sort by ${col.label}`}
                  className="flex items-center gap-1 font-semibold hover:text-slate-200 select-none w-full"
                >
                  {col.label}
                  {renderSortIcon(col.key)}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groupByDispatcher ? (
            // Grouped view
            sortedGroups.map((group) => (
              <React.Fragment key={group.dispatcher}>
                {/* Dispatcher header row */}
                <tr
                  className="bg-gray-100 dark:bg-gray-700 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                  onClick={() => toggleDispatcher(group.dispatcher)}
                >
                  <td colSpan={COLUMNS.length + 1} className="p-2">
                    <div className="flex items-center gap-2">
                      <ChevronRightIcon
                        className={`h-4 w-4 text-gray-500 transition-transform ${
                          expandedDispatchers.has(group.dispatcher) ? 'rotate-90' : ''
                        }`}
                      />
                      <span className="font-medium text-heading">
                        {group.dispatcher}
                      </span>
                      <span className="text-sm text-muted">
                        ({group.drivers.length} driver{group.drivers.length !== 1 ? 's' : ''})
                      </span>
                    </div>
                  </td>
                </tr>
                {/* Driver rows */}
                {expandedDispatchers.has(group.dispatcher) &&
                  group.drivers.map((driver) => renderDriverRow(driver, true))}
              </React.Fragment>
            ))
          ) : (
            // Flat view
            flatDriverList.map((driver) => renderDriverRow(driver, false))
          )}
        </tbody>
      </table>
      {/* Footer with totals over the visible rows */}
      <div
        data-testid="grounded-yard-footer"
        className="sticky bottom-0 bg-gray-100 dark:bg-gray-700 border-t border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>
            Total: <span className="font-semibold">{totalCount}</span> driver{totalCount !== 1 ? 's' : ''} grounded at yard
          </span>
          {statsOk && (
            <>
              <span className="text-gray-400">·</span>
              <span>Started <span className="font-semibold">{visibleTotals.started}</span> of {totalCount}</span>
              <span className="text-gray-400">·</span>
              <span>CY picked <span className="font-semibold">{visibleTotals.cy_picked}</span> of {visibleTotals.cy_planned}</span>
              <span className="text-gray-400">·</span>
              <span>Open legs <span className="font-semibold">{visibleTotals.open_legs}</span></span>
              <span className="text-gray-400">·</span>
              <span>
                Declined{' '}
                <span className={`font-semibold ${visibleTotals.declined > 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {visibleTotals.declined}
                </span>
              </span>
            </>
          )}
        </div>
        {statsNote && (
          <div className="text-xs text-muted mt-1">{statsNote}</div>
        )}
      </div>
    </div>
  );
};

export default DriverGroundedYard;
